import { DEFAULT_LINE_WIDTH, LINE_WIDTH_TO_VIEWPORT_RATIO } from '@/lib/annotations/constants'

type ObjectFit = 'contain' | 'cover' | 'none'

export class AnnotationCanvas implements DrawingObserver {
  private ctx: CanvasRenderingContext2D
  /*
   * While the actual DOM element takes up the size of its container, the virtualSize
   * is the size of the element we are annotating. (e.g. a PDF slide)
   *
   * Normalized coordinates are relative to this dimension. When annotations occur
   * outside of the content box (but within the container), coordinates can be >1 or <-1.
   */
  private virtualSize: Size = {
    width: 0,
    height: 0,
  }
  private size: Size = {
    width: 0,
    height: 0,
  }
  private aspectRatio = 4 / 3
  /*
   * Modeled after the CSS object-fit property.
   * 'contain' means the content is fit within the container frame.
   * 'cover' means the container frame is fit within the content, i.e. the content is not all visible.
   * Cover is used for 3D slides.
   */
  private objectFit: ObjectFit = 'contain'
  private lineWidth = DEFAULT_LINE_WIDTH
  private lastPoint: Point = { x: 0, y: 0 }

  constructor(private canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d')
    if (!context) throw new Error("Couldn't get canvas context")

    this.ctx = context
    this.ctx.lineCap = 'round'
    this.ctx.lineJoin = 'round'
  }

  resize(width: number, height: number) {
    const strokeStyle = this.getSetup().strokeStyle

    // Ensure size is an integer and at least 1 x 1
    // Non-integer values can cause blurring
    width = Math.floor(Math.max(1, width))
    height = Math.floor(Math.max(1, height))

    this.size = { width, height }

    // multiply the size of the canvas by the DPR. This enables high res image for devices / high pixel screens
    const dpr = window.devicePixelRatio
    this.canvas.width = width * dpr
    this.canvas.height = height * dpr

    // use CSS to shrink the canvas back to the proper size (pre-DPR adjustment)
    this.canvas.style.width = `${width}px`
    this.canvas.style.height = `${height}px`
    this.ctx.setTransform(1, 0, 0, 1, 0, 0)
    this.ctx.translate(this.canvas.width / 2, this.canvas.height / 2)
    this.ctx.scale(dpr, dpr)

    // recalculate using aspect ratio, object fit, and updated width/height
    this.setVirtualSize()

    // not only does setting width clear the canvas, it also clears any context settings
    this.setup({ strokeStyle })
    this.setupLineWidth()
  }

  setAspect(aspectRatio: number) {
    // no need to replay / utilize history, this is only triggered on slide change so we reset anyway.
    // setAspect is NOT called when the viewport resizes, only when the content (slide) changes
    this.aspectRatio = aspectRatio
    this.setVirtualSize()
    this.setupLineWidth()
  }

  setObjectFit(mode: ObjectFit) {
    this.objectFit = mode
    this.setVirtualSize()
    this.setupLineWidth()
  }

  private setVirtualSize() {
    if (this.objectFit === 'contain') {
      this.virtualSize = {
        width: Math.min(this.size.width, this.size.height * this.aspectRatio),
        height: Math.min(this.size.height, this.size.width / this.aspectRatio),
      }
    } else if (this.objectFit === 'cover') {
      //- While this is technically the correct answer, it doesn't gel with our
      //- socket schema for 3D slides.
      //
      // this.virtualSize = {
      //   width: Math.max(this.size.width, this.size.height * this.aspectRatio),
      //   height: Math.max(this.size.height, this.size.width / this.aspectRatio),
      // }
      this.virtualSize = {
        width: this.size.height * 2,
        height: this.size.height * 2,
      }
    }
  }

  getSetup() {
    return {
      strokeStyle: this.ctx.strokeStyle as string, // technically can be a gradient or pattern
      lineWidth: this.ctx.lineWidth,
    }
  }

  /*
   * Normalize point relative to the canvas's virtual size (see note above).
   * - (0, 0) is the centre of the canvas
   * - (1, 1) is bottom right corner
   * - (-1, -1) is top right corner
   *
   * Absolute value may exceed 1 since annotations can extend beyond the virtual
   * size (up to the DOM size) when objectFit = 'contain'.
   */
  normalize(pt: Point): Point {
    return {
      x: (pt.x * 2 - this.size.width) / this.virtualSize.width,
      y: (pt.y * 2 - this.size.height) / this.virtualSize.height,
    }
  }

  private denormalize(pt: Point): Point {
    return {
      x: (pt.x * this.virtualSize.width) / 2,
      y: (pt.y * this.virtualSize.height) / 2,
    }
  }

  addEventListener<K extends keyof HTMLElementEventMap>(type: K, listener: (ev: HTMLElementEventMap[K]) => any): void {
    this.canvas.addEventListener(type, listener)
  }

  removeEventListener<K extends keyof HTMLElementEventMap>(type: K, listener: (ev: HTMLElementEventMap[K]) => any): void {
    this.canvas.removeEventListener(type, listener)
  }

  start(coords: Point) {
    const denormalized = this.denormalize(coords)
    this.ctx.beginPath()
    this.ctx.moveTo(denormalized.x, denormalized.y)
    this.lastPoint = denormalized
  }

  move(coords: Point) {
    const denormalized = this.denormalize(coords)
    const midX = (this.lastPoint.x + denormalized.x) / 2
    const midY = (this.lastPoint.y + denormalized.y) / 2
    this.ctx.quadraticCurveTo(this.lastPoint.x, this.lastPoint.y, midX, midY)
    this.lastPoint = denormalized
  }

  stop() {
    this.ctx.stroke()
  }

  setup(opts: SetupOpts) {
    if (typeof opts.strokeStyle === 'string') {
      this.ctx.strokeStyle = opts.strokeStyle
    }
    if (typeof opts.lineWidth === 'number' && this.lineWidth !== opts.lineWidth) {
      this.lineWidth = opts.lineWidth
      this.setupLineWidth()
    }
  }

  private setupLineWidth() {
    // Line width is proportional to canvas size. This is to preserve
    // appearance of annotations across devices and resize events.
    let screenSize = Math.min(this.virtualSize.height, this.virtualSize.width)

    // A little bit hacky but need to account for the 3D/site slide mode defined by socket.
    if (this.objectFit === 'cover') {
      screenSize = this.size.height
    }

    this.ctx.lineWidth = Math.floor(screenSize * this.lineWidth * LINE_WIDTH_TO_VIEWPORT_RATIO)
  }

  reset() {
    const { width, height } = this.size
    this.ctx.clearRect(-width / 2, -height / 2, width, height)
    this.ctx.beginPath() // clears any currently active path in the context
  }

  get _virtualSize() {
    return {
      width: this.virtualSize.width,
      height: this.virtualSize.height,
    }
  }
}
